import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';

import {
  fetchMetrics,
  fetchMetricsByIds
} from '../../../services/syntiro_honey/data_ingestion.service';
import { MetricLookupItem } from '../../../types/syntiro_honey';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';

const SEARCH_DEBOUNCE_MS = 300;

interface MetricAutocompleteProps {
  /** Field label. */
  label: string;
  /** The selected metric id, or null when nothing is selected. */
  selectedId: string | null;
  /**
   * Display name for `selectedId` when it was chosen outside this control
   * (e.g. a mapping candidate); falls back to the raw id while unknown.
   */
  selectedLabel?: string | null;
  /** Called with the chosen metric (or null when cleared). */
  onSelect: (metric: MetricLookupItem | null) => void;
  /**
   * Called with the name this control resolved for a `selectedId` it was not
   * given a label for, so the caller can show the same name elsewhere.
   */
  onResolveLabel?: (metric: MetricLookupItem) => void;
  /** Disable the control. */
  disabled?: boolean;
}

/** The full option text, shown on hover where the row itself is clipped. */
function describeMetric(option: MetricLookupItem): string {
  const details = [option.standardName, option.metricGroup, option.unit ?? undefined]
    .filter((part) => part !== undefined && part !== '')
    .join(' · ');
  return details === '' ? option.name : `${option.name} — ${details}`;
}

/**
 * Server-backed catalogue search: an Autocomplete over `GET /metrics` so a
 * metric is picked by name instead of by pasting a UUID. Options load as the
 * user types (debounced) and on open.
 * @returns {JSX.Element}
 */
export default function MetricAutocomplete({
  label,
  selectedId,
  selectedLabel,
  onSelect,
  onResolveLabel,
  disabled
}: MetricAutocompleteProps) {
  const [options, setOptions] = useState<Array<MetricLookupItem>>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [catalogueUnavailable, setCatalogueUnavailable] = useState<boolean>(false);
  // Metrics resolved by id rather than by search — the selection may not be in the
  // first page of results, and this control is remounted (losing its options) every
  // time the filter popover closes.
  const [resolvedById, setResolvedById] = useState<Map<string, MetricLookupItem>>(new Map());

  const known =
    selectedId === null
      ? undefined
      : (options.find((option) => option.id === selectedId) ?? resolvedById.get(selectedId));
  // While the selection is unresolved, MUI shows its id in the field. Searching the
  // catalogue for a UUID matches nothing, which would strand the field on the id
  // (Maxine 2026-08-20: the metric filter showed a code, not the metric name).
  const searchTerm = inputValue === selectedId && known === undefined ? '' : inputValue;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCatalogueUnavailable(false);
    const handle = setTimeout(() => {
      fetchMetrics(searchTerm, 20)
        .then((items) => {
          if (!cancelled) {
            setOptions(items);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setCatalogueUnavailable(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchTerm]);

  // Resolve a selection the search did not return, so the field names the metric
  // instead of echoing its id.
  useEffect(() => {
    if (selectedId === null || known !== undefined) {
      return undefined;
    }
    let cancelled = false;
    fetchMetricsByIds([selectedId])
      .then((items) => {
        const [metric] = items;
        if (cancelled || metric === undefined) {
          return;
        }
        setResolvedById((current) => new Map(current).set(metric.id, metric));
        onResolveLabel?.(metric);
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogueUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // onResolveLabel is a render-scoped callback; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, known]);

  // Represent the current selection as an option even when it is not in the
  // fetched page, so the Autocomplete stays controlled without warnings.
  const value = useMemo<MetricLookupItem | null>(() => {
    if (selectedId === null) {
      return null;
    }
    if (known !== undefined) {
      return known;
    }
    return {
      id: selectedId,
      name: selectedLabel ?? selectedId,
      metricGroup: '',
      standardName: '',
      unit: null,
      isQualitative: false
    };
  }, [selectedId, selectedLabel, known]);

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 260 }}
      options={options}
      value={value}
      loading={loading}
      disabled={disabled}
      onChange={(_event, next) => onSelect(next)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterOptions={(fetched) => fetched}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {/* Long catalogue names are clipped by the field width, so the full text
              is available on hover (Maxine 2026-08-20). */}
          <Tooltip title={describeMetric(option)} placement="right">
            {/* span carries the ref Tooltip needs; the typography wrapper cannot */}
            <span style={{ overflow: 'hidden', display: 'block', width: '100%' }}>
              <BodyText noWrap>{option.name}</BodyText>
              <CaptionText noWrap>
                {option.standardName} · {option.metricGroup}
                {option.unit !== null && ` · ${option.unit}`}
                {option.isQualitative && ' · qualitative'}
              </CaptionText>
            </span>
          </Tooltip>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={
            catalogueUnavailable ? 'Metric catalogue unavailable — try again shortly' : undefined
          }
        />
      )}
    />
  );
}
